export type Severity = 'critical' | 'warning' | 'info'
export type ShipmentStatus =
  | 'Critical'
  | 'At risk'
  | 'Customs hold'
  | 'On track'

export type Vessel = {
  id: string
  name: string
  route: string
  x: number
  y: number
  heading: number
  speed: string
  eta: string
  status: 'moving' | 'delayed' | 'anchored'
}

export type AlertItem = {
  id: string
  severity: Severity
  title: string
  detail: string
  time: string
  code: string
}

export type Shipment = {
  id: string
  container: string
  origin: string
  destination: string
  vessel: string
  eta: string
  delay: string
  status: ShipmentStatus
  priority: 'P1' | 'P2'
  cargo: string
}

export const vessels: Vessel[] = [
  {
    id: 'v1',
    name: 'MV Asterion',
    route: 'SGP → RTM',
    x: 73,
    y: 45,
    heading: 18,
    speed: '18.4 kn',
    eta: '16 JUL 08:40',
    status: 'moving',
  },
  {
    id: 'v2',
    name: 'Nordic Titan',
    route: 'SHA → LAX',
    x: 81,
    y: 38,
    heading: 278,
    speed: '14.1 kn',
    eta: '13 JUL 22:10',
    status: 'delayed',
  },
  {
    id: 'v3',
    name: 'Orion Vale',
    route: 'RTM → NYC',
    x: 47,
    y: 32,
    heading: 260,
    speed: '16.8 kn',
    eta: '11 JUL 14:20',
    status: 'moving',
  },
  {
    id: 'v4',
    name: 'Kestrel Bay',
    route: 'DXB → SIN',
    x: 62,
    y: 50,
    heading: 110,
    speed: '0.0 kn',
    eta: 'HOLD',
    status: 'anchored',
  },
  {
    id: 'v5',
    name: 'Pacific Crown',
    route: 'LAX → YOK',
    x: 18,
    y: 41,
    heading: 265,
    speed: '19.2 kn',
    eta: '14 JUL 03:15',
    status: 'moving',
  },
]

export const initialAlerts: AlertItem[] = [
  {
    id: 'a1',
    severity: 'critical',
    title: 'Reefer temperature excursion',
    detail: 'TCLU 482901-7 · +3.8°C above threshold',
    time: '02m',
    code: 'COLD-7',
  },
  {
    id: 'a2',
    severity: 'critical',
    title: 'Customs examination ordered',
    detail: '4 priority units · Port of Long Beach',
    time: '11m',
    code: 'CUS-4',
  },
  {
    id: 'a3',
    severity: 'warning',
    title: 'Berth window at risk',
    detail: 'MV Nordic Titan · projected slip 9h 20m',
    time: '18m',
    code: 'PORT-2',
  },
  {
    id: 'a4',
    severity: 'warning',
    title: 'Typhoon route deviation',
    detail: 'Pacific Crown · +214 nm alternate route',
    time: '37m',
    code: 'WX-9',
  },
  {
    id: 'a5',
    severity: 'info',
    title: 'Transshipment complete',
    detail: '18 P2 units cleared at Singapore',
    time: '51m',
    code: 'OPS-1',
  },
]

export const shipments: Shipment[] = [
  {
    id: 'GSX-847291',
    container: 'TCLU 482901-7',
    origin: 'Singapore',
    destination: 'Rotterdam',
    vessel: 'MV Asterion',
    eta: '16 JUL 08:40',
    delay: '+18h 20m',
    status: 'Critical',
    priority: 'P1',
    cargo: 'Pharma / cold chain',
  },
  {
    id: 'GSX-190433',
    container: 'MSCU 771204-2',
    origin: 'Shanghai',
    destination: 'Los Angeles',
    vessel: 'Nordic Titan',
    eta: '13 JUL 22:10',
    delay: '+9h 20m',
    status: 'At risk',
    priority: 'P1',
    cargo: 'Automotive parts',
  },
  {
    id: 'GSX-663872',
    container: 'CMAU 215900-4',
    origin: 'Dubai',
    destination: 'Singapore',
    vessel: 'Kestrel Bay',
    eta: 'PENDING',
    delay: 'Customs',
    status: 'Customs hold',
    priority: 'P1',
    cargo: 'Aerospace systems',
  },
  {
    id: 'GSX-440928',
    container: 'OOLU 009341-8',
    origin: 'Rotterdam',
    destination: 'New York',
    vessel: 'Orion Vale',
    eta: '11 JUL 14:20',
    delay: '+2h 05m',
    status: 'At risk',
    priority: 'P2',
    cargo: 'Industrial tooling',
  },
  {
    id: 'GSX-302177',
    container: 'MAEU 882130-1',
    origin: 'Los Angeles',
    destination: 'Yokohama',
    vessel: 'Pacific Crown',
    eta: '14 JUL 03:15',
    delay: '—',
    status: 'On track',
    priority: 'P2',
    cargo: 'Semiconductors',
  },
  {
    id: 'GSX-990214',
    container: 'HLCU 733921-0',
    origin: 'Busan',
    destination: 'Hamburg',
    vessel: 'MV Asterion',
    eta: '17 JUL 18:30',
    delay: '+6h 10m',
    status: 'At risk',
    priority: 'P2',
    cargo: 'Medical devices',
  },
]

export const ports = [
  {
    code: 'LAX',
    name: 'Los Angeles',
    utilization: 94,
    wait: '42h',
    trend: '+12%',
  },
  {
    code: 'SGP',
    name: 'Singapore',
    utilization: 87,
    wait: '18h',
    trend: '+4%',
  },
  { code: 'RTM', name: 'Rotterdam', utilization: 72, wait: '9h', trend: '−3%' },
  { code: 'SHA', name: 'Shanghai', utilization: 68, wait: '7h', trend: '−6%' },
]

export const customs = [
  { port: 'USLGB', units: 14, age: '31h', reason: 'X-ray examination' },
  { port: 'AEDXB', units: 8, age: '19h', reason: 'Document mismatch' },
  { port: 'NLRTM', units: 5, age: '12h', reason: 'HS code review' },
]
